<cdk-virtual-scroll-viewport
    class="agpr-virtualScroll"
    [itemSize]="vsItemSize"
    [minBufferPx]="vsMinBufferPx"
    [maxBufferPx]="vsMaxBufferPx"
>
    <table
        role="grid"
        class="agpr-table"
        [attr.aria-rowcount]="columns.length + 1"
    >
        <caption class="u-srOnly">
            {{ 'Common.table.arrowKeyNavigation' | localize }}
            {{ 'Common.table.sortDescription' | localize }}
        </caption>
        <thead
            #tableHeader
            *ngIf="showHeader"
            class="agpr-tableHead"
        >
            <tr aria-rowindex="1">
                <th
                    *ngFor="let column of columns; let first = first; let i = index"
                    role="columnheader"
                    id="{{id}}_tableHeader_{{i}}"
                    class="agpr-tableHead-cell {{ column.styleClass }}"
                    [ngClass]="{
                        'agpr-tableHead-cell--sortable': column.sortable
                    }"
                    [ngStyle]="{
                        'width.px': column.width,
                        'paddingLeft.px': column.headerPaddingLeft
                    }"
                    tabindex="0"
                    [attr.aria-sort]="currentSortColumn === column ? (currentSortAscending ? 'ascending' : 'descending') : null"
                    (keydown)="$event.stopPropagation()"
                    (keyup)="handleHeaderKeyboardNavigation($event, column, i)"
                >
                    <div
                        class="agpr-tableInput"
                    >
                        <ng-container *ngIf="enableSelectAll && first">
                            <agpr-checkbox
                                [id]="id + '_th_' + i + '_checkbox'"
                                class="agpr-table-checkbox agpr-table-cell--checkbox"
                                [binary]="true"
                                [initiallyFocusable]="false"
                                [(ngModel)]="selectAll"
                                (ngModelChange)="toggleSelectAll($event)"
                            ></agpr-checkbox>
                        </ng-container>
                        <!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->
                        <span
                            class="agpr-tableHead-cellSpan"
                            (click)="toggleSort(column)"
                        >
                            {{ column.heading | localize }}
                            <span
                                *ngIf="column.sortable"
                                role="img"
                                class="agpr-table-sortToggle fa-solid"
                                [ngClass]="{
                                    'fa-arrows-up-down': currentSortColumn !== column,
                                    'fa-arrow-up': currentSortColumn === column && currentSortAscending,
                                    'fa-arrow-down': currentSortColumn === column && !currentSortAscending
                                }"
                            ></span>
                            <span
                                *ngIf="column.headingInfoTooltip"
                                role="img"
                                class="fa-regular fa-circle-info agpr-table-headingInfoCircle"
                                [agprTooltip]="column.headingInfoTooltip"
                            ></span>
                        </span>
                        <!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->
                    </div>
                </th>
                <th
                    *ngIf="showDelete || showView"
                    id="{{id}}_tableActionHeader"
                    class="agpr-tableHead-cell"
                    tabindex="0"
                    (keyup)="handleHeaderKeyboardNavigation($event, 'actionHeader')"
                >
                    {{ 'action' | localize }}
                </th>
            </tr>
        </thead>
        <tbody class="agpr-tableBody">
            <tr
                *ngIf="sortedData.length === 0 && noContentMessage"
            >
                <td
                    class="agpr-table-noContentCell"
                    [attr.colspan]="columns.length + (showActionCell ? 1 : 0)"
                >
                    <agpr-message
                        [severity]="messageSeverity.noContent"
                    >{{ noContentMessage | localize }}</agpr-message>
                </td>
            </tr>
            <tr
                *cdkVirtualFor="let row of sortedData; let first = first; let last = last; let i = index"
                class="agpr-table-row"
                (click)="onRowClick(row, $event)"
                [ngClass]="{
                    'agpr-table-row--active': row === selectedRow,
                    'agpr-table-row--selectableRow': selectableRow,
                    'agpr-table-row--selected': row.selected,
                    'agpr-table-row--highlighted': row === highlightedRow
                }"
                [attr.aria-rowindex]="i + 2"
            >
                <td
                    *ngFor="let column of columns; let j = index"
                    role="gridcell"
                    id="{{id}}_tableDataCell_{{i}}_{{j}}"
                    class="agpr-table-cell {{ column.cellStyleClass }}"
                    [ngClass]="{ 'agpr-table-cell--firstRow': first, 'agpr-table-cell--lastRow': last }"
                    [ngSwitch]="column.type"
                    [ngStyle]="{
                        'width.px': column.width,
                        'max-width.px': column.maxWidth
                    }"
                    [pTooltip]="row[column.tooltipField]"
                    tooltipPosition="top"
                    [tooltipDisabled]="editingRow === row && editingColumn === column"
                    [attr.aria-describedby]="rowSelectedAriaLabel ? id + '_rowSelectedHiddenDescription' : null"
                    tabindex="-1"
                    (keydown)="$event.stopPropagation()"
                    (keyup)="handleDataCellKeyboardNavigation($event, i, j)"
                >
                    <div
                        class="agpr-table-cellContainer"
                    >
                        <agpr-checkbox
                            *ngSwitchCase="columnTypes.checkbox"
                            [id]="id + '_' + i + '_' + j + '_checkbox'"
                            class="agpr-table-checkbox"
                            [ngClass]="{ 'agpr-table-cell--checkbox': column.type === columnTypes.checkbox }"
                            [label]="row[column.labelField]"
                            [binary]="true"
                            [disabled]="row?.editDisabled"
                            [initiallyFocusable]="false"
                            [(ngModel)]="row[column.field]"
                            (ngModelChange)="saveCheckboxValue($event, column, row)"
                        ></agpr-checkbox>
                        <div
                            *ngSwitchCase="columnTypes.text"
                            class="agpr-tableInput"
                        >
                            <ng-container *ngIf="column.selectable">
                                <agpr-checkbox
                                    class="agpr-table-checkbox"
                                    [id]="id + '_' + i + '_' + j + '_checkbox'"
                                    [ngClass]="{ 'agpr-table-cell--checkbox': column.selectable }"
                                    [binary]="true"
                                    [initiallyFocusable]="false"
                                    [(ngModel)]="row[column.selectField]"
                                    (ngModelChange)="saveCheckboxValue($event, column, row, group)"
                                ></agpr-checkbox>
                            </ng-container>
                            <ng-template [ngIf]="editingRow === row && editingColumn === column">
                                <agpr-text-input
                                    [(ngModel)]="row[column.field]"
                                    (keydown)="saveOnEnter($event, column, row)"
                                    (blur)="saveValue(column, row)"
                                    [maxLength]="column.fieldMaxLength"
                                    [initialFocus]="true"
                                    [bottomMargin]="false"
                                    [selectOnEdit]="true"
                                    [ariaLabel]="column.heading"
                                ></agpr-text-input>
                            </ng-template>
                            <ng-template [ngIf]="editingRow !== row || editingColumn !== column">
                                <span
                                    class="agpr-tableInput-value"
                                    [ngClass]="{
                                        'agpr-tableInput-value--sortable': column.sortable,
                                        'agpr-tableInput-value--withIcon': column.iconField && row[column.iconField]
                                    }"
                                    [agprTooltip]="row.tooltip"
                                    tooltipPosition="top"
                                >
                                    {{ row[column.field] }}
                                </span>
                                @if (column.iconField && row[column.iconField]) {
                                    <span
                                        role="img"
                                        class="agpr-tableInput-rowIcon"
                                        [ngClass]="row[column.iconField]"
                                        [agprTooltip]="row[column.iconTooltipField]"
                                        tooltipPosition="top"
                                        [initiallyFocusable]="false"
                                        [attr.aria-label]="row[column.iconTooltipField]"
                                    ></span>
                                }
                                <agpr-button
                                    *ngIf="!row.editDisabled"
                                    class="agpr-tableInput-icon"
                                    [style]="buttonTypes.icon"
                                    icon="fa-regular fa-duotone fa-pen"
                                    tooltip="rename"
                                    [initiallyFocusable]="false"
                                    (click)="editField(column, row, $event)"
                                ></agpr-button>
                            </ng-template>
                        </div>
                        <div
                            *ngSwitchCase="columnTypes.textarea"
                            class="agpr-tableInput"
                            [ngClass]="{ 'agpr-tableInput--withCustomTemplate': column.customTemplate }"
                        >
                            <ng-container
                                [ngTemplateOutlet]="column.customTemplate"
                                [ngTemplateOutletContext]="{column:column, row:row}"
                            ></ng-container>
                            <ng-container>
                                <ng-template [ngIf]="editingRow === row && editingColumn === column">
                                    <agpr-textarea-input
                                        [(ngModel)]="row[column.field]"
                                        (keypress)="saveOnEnter($event, column, row)"
                                        (blur)="saveValue(column, row)"
                                        [maxlength]="column.fieldMaxLength"
                                        [initialFocus]="true"
                                        [bottomMargin]="false"
                                        [ariaLabel]="column.heading"
                                    ></agpr-textarea-input>
                                </ng-template>
                                <ng-template [ngIf]="editingRow !== row || editingColumn !== column">
                                    <div class="agpr-tableInput-flexContainer">
                                        <span class="agpr-tableInput-textarea">
                                            {{ row[column.field] }}
                                        </span>
                                        <agpr-button
                                            class="agpr-tableInput-icon"
                                            [style]="buttonTypes.icon"
                                            icon="fa-regular fa-duotone fa-pen"
                                            [initiallyFocusable]="false"
                                            (click)="editField(column, row, $evemt)"
                                        ></agpr-button>
                                    </div>
                                </ng-template>
                            </ng-container>
                        </div>
                        <div
                            *ngSwitchCase="columnTypes.linkList"
                        >
                            <agpr-link-list
                                *ngIf="row[column.field]; else noLinks"
                                class="agpr-tableInput-value"
                                [links]="row[column.field]"
                                [itemsMessage]="column.message"
                                [listAriaLabel]="column.listAriaLabel"
                                [id]="id + '_' + i + '_' + j + '_linkList'"
                            ></agpr-link-list>

                            <ng-template #noLinks>
                                <span class="agpr-tableInput-value--emptyLinks">
                                    --
                                </span>
                            </ng-template>
                        </div>
                        <div
                            *ngSwitchCase="columnTypes.custom"
                        >
                            <ng-container
                                [ngTemplateOutlet]="column.customTemplate"
                                [ngTemplateOutletContext]="{column:column, columnIndex:j, row:row, rowIndex:i, selectedRow:selectedRow}"
                            ></ng-container>
                        </div>
                        <span
                            *ngSwitchDefault
                            [ngClass]="{'agpr-tableInput-value--sortable': column.sortable}"
                        >
                            <ng-container
                                *ngIf="row[column.field] else nullPlaceholder"
                                [ngSwitch]="column.format"
                            >
                                <ng-container
                                    *ngSwitchCase="rowFormats.date"
                                >
                                    {{ row[column.field] | changeTimeZone : userPreferences?.timeZone : defaultDateFormat }}
                                </ng-container>
                                <ng-container
                                    *ngSwitchDefault
                                >
                                    {{ row[column.field] }}
                                </ng-container>
                            </ng-container>
                            <ng-template #nullPlaceholder>
                                {{ tablePlaceholders.NULL_PLACEHOLDER }}
                            </ng-template>
                        </span>
                        <span
                            *ngIf="column.showInfoCircle"
                            role="img"
                            class="fa-regular fa-circle-info agpr-table-infoCircle"
                            [agprTooltip]="row.tooltipInfo"
                            tooltipPosition="top"
                            [initiallyFocusable]="false"
                            (keyup)="handleDataCellKeyboardNavigation($event, i)"
                        ></span>
                    </div>
                </td>
                <td
                    *ngIf="showActionCell"
                    id="{{id}}_tableActionCell_{{i}}"
                    class="agpr-table-cell agpr-table-actions"
                    [ngClass]="{'agpr-table-cell--firstRow': first, 'agpr-table-cell--lastRow': last }"
                    [style.width]="actionItemWidth"
                    [style.textAlign]="'center'"
                    tabindex="-1"
                >
                    @if (actionMenu) {
                        <agpr-kebab-menu
                            class="agpr-tableRow-actionMenu"
                            [id]="id + '_tableActionMenu_' + i"
                            [options]="actionMenuOptions"
                            [data]="row"
                            [showIcon]="true"
                            [textAlignment]="actionMenuTextAlignment"
                            [ariaLabel]="'Discover.leftSidebar.listItemAction.buttonAriaLabel' | localize"
                            panelPositionX="left"
                        ></agpr-kebab-menu>
                    } @else {
                    <agpr-button
                        *ngIf="showView"
                        class="agpr-tableRow-action"
                        [style]="buttonTypes.icon"
                        icon="{{viewIcon}}"
                        [tooltip]="viewIconTooltip"
                        [initiallyFocusable]="false"
                        (keydown)="$event.stopPropagation()"
                        (keyup)="handleActionCellKeyboardNavigation($event, i)"
                        (click)="view(row)"
                    ></agpr-button>
                    <agpr-button
                        *ngIf="showDelete"
                        class="agpr-tableRow-action"
                        [style]="buttonTypes.icon"
                        icon="fa-regular fa-duotone fa-trash-can"
                        [tooltip]="deleteLabel"
                        [initiallyFocusable]="false"
                        (keydown)="$event.stopPropagation()"
                        (keyup)="handleActionCellKeyboardNavigation($event, i)"
                        (click)="deleteItem(row, i)"
                    ></agpr-button>
                    }
                </td>
            </tr>
        </tbody>
    </table>
    <span
        *ngIf="rowSelectedAriaLabel"
        [id]="id + '_rowSelectedHiddenDescription'"
        class="u-srOnly"
    >
        {{ rowSelectedAriaLabel | localize }}
    </span>
</cdk-virtual-scroll-viewport>
